// src/components/SystemRecommendation/SystemRecommendation.tsx

import React, { useEffect, useState } from 'react';
import { RecommendationData, ViewMode } from './types';
import HeroCard from './components/HeroCard';
import WhyThisSystem from './components/WhyThisSystem';
import WhatChanges from './components/WhatChanges';
import AlternativesConsidered from './components/AlternativesConsidered';
import AssumptionsSection from './components/AssumptionsSection';
import NextSteps from './components/NextSteps';
import ViewModeToggle from './components/ViewModeToggle';
import FitsYourHouse from './components/FitsYourHouse';
import FitsYourFamily from './components/FitsYourFamily';
import FitsYourFuture from './components/FitsYourFuture';
import FitsYourBankAccount from './components/FitsYourBankAccount';
import ExecutiveSummary from './components/ExecutiveSummary';
// TODO: CSS import disabled temporarily to fix build
// The TypeScript compiler doesn't copy non-TS files to dist/
// Options to fix:
// 1. Add a build step to copy CSS files to dist/
// 2. Import CSS in the consuming app (standalone-web) instead
// 3. Use CSS-in-JS solution
// Since SystemRecommendation is not currently used in standalone-web, this is safe for now
// import './SystemRecommendation.css';

interface SystemRecommendationProps {
  data: RecommendationData;
  onGeneratePDF?: (mode: ViewMode) => void;
}

const SystemRecommendation: React.FC<SystemRecommendationProps> = ({
  data,
  onGeneratePDF
}) => {
  const [viewMode, setViewMode] = useState<ViewMode>('customer');
  const isExploratory = data.engineState === 'exploratory';

  useEffect(() => {
    if (isExploratory && viewMode === 'technical') {
      setViewMode('customer');
    }
  }, [isExploratory, viewMode]);

  const handleToggle = (mode: ViewMode) => {
    if (mode === 'technical' && isExploratory) {
      return;
    }
    setViewMode(mode);
  };

  return (
    <div className={`system-recommendation ${viewMode}-mode`}>
      <ViewModeToggle
        currentMode={viewMode}
        onToggle={handleToggle}
        onGeneratePDF={onGeneratePDF}
        technicalDisabled={isExploratory}
        technicalDisabledReason="Expert Mode requires mains pressure and flow rate data to display."
      />

      <div className="recommendation-flow">
        {/* Executive Summary at the top */}
        <ExecutiveSummary
          recommendedSystem={data.systemName}
          whyThisWins={data.whyThisWins}
          expectedGasUse={data.expectedGasUse}
          expectedElectricityUse={data.expectedElectricityUse}
          standingLoss={data.standingLoss}
          scop={data.scop}
          confidence={data.confidence.level.charAt(0).toUpperCase() + data.confidence.level.slice(1) as 'High' | 'Medium' | 'Low'}
          confidenceInputs={data.confidenceInputs}
          heatPumpReady={data.futureFit?.upgradePath.futureProof}
          viewMode={viewMode}
        />

        <HeroCard
          data={data}
          viewMode={viewMode}
        />

        {/* New customer-centric sections */}
        {data.houseFit && (
          <FitsYourHouse
            data={data.houseFit}
            viewMode={viewMode}
          />
        )}

        {data.familyFit && (
          <FitsYourFamily
            data={data.familyFit}
            viewMode={viewMode}
          />
        )}

        {data.futureFit && (
          <FitsYourFuture
            data={data.futureFit}
            viewMode={viewMode}
          />
        )}

        {data.financialFit && (
          <FitsYourBankAccount
            data={data.financialFit}
            viewMode={viewMode}
          />
        )}

        {/* Existing sections */}
        <WhyThisSystem
          reasons={data.reasons}
          viewMode={viewMode}
          assumptions={data.assumptions}
        />

        <WhatChanges
          changes={data.installationScope}
          viewMode={viewMode}
        />

        <AlternativesConsidered
          alternatives={data.alternatives}
          viewMode={viewMode}
        />

        <AssumptionsSection
          assumptions={data.assumptions}
          confidence={data.confidence}
          viewMode={viewMode}
        />

        <NextSteps
          viewMode={viewMode}
          price={data.pricing}
        />
      </div>
    </div>
  );
};

export default SystemRecommendation;
